/**
 * SadhanaPage — real React replacement for the /sadhana iframe mockup.
 *
 * Staged at /sadhana-v2. /sadhana continues to serve the iframe mockup
 * until the founder authorises the flip.
 *
 * DISPLAY-ONLY v1: no tracking, no streaks, no check-ins, no writes.
 *   - READS icraftz.sankalpa.commit.v1[bookId] (fresh on every mount).
 *   - RE-POSTs /api/mycraftz-install/ideas to hydrate titles/descriptions
 *     for the 3 committed idea ids (cache hit, fast).
 *   - Never writes any localStorage key. Never touches any backend beyond
 *     the read-only /ideas call. Never renders a checkbox or progress UI.
 *   - committedAt is used ONLY as a static formatted date label — no
 *     elapsed-time math, no Day-N calculation.
 *
 * Three explicit states, each with its own top-level testid:
 *   (a) commit exists       → 3 typographic practice cards
 *   (b) book, no commit     → "Sadhana needs a Sankalpa" designed page
 *   (c) no book param       → empty state
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { CharCount } from "../components/CharCount";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { ArrowRight, RefreshCw, BookOpen, Check, ChevronDown, Flame, LifeBuoy, Feather, Calendar, Hourglass, Sparkles, ScrollText } from "lucide-react";
import { revealTierForDays } from "../lib/demoCraftPrint";
import "../styles/MyCraftzPage.css";

import Navigation from "../components/homev3/Navigation";
import Footer from "../components/homev3/Footer";
import GoldRule from "../components/homev3/GoldRule";
import ParchmentBackground from "../components/ParchmentBackground";
import { useLanguage } from "../lib/useLanguage";
import { useAuth } from "../context/AuthContext";
import {
  SADHANA_TARGET_DAYS,
  UPAYA_PRESET_CHIPS,
  readAnonCompletions,
  toggleAnonCompletion,
} from "../lib/sadhanaCompletion";
import { withTransientRetry } from "../lib/apiRetry";
import { T, R } from "../components/homev3/tokens";
import {
  CARD_BG,
  CARD_BORDER,
  CARD_RADIUS,
  CARD_SHADOW,
  StageKicker,
  StageBottomBanner,
  StageEmptyStateFlank,
} from "../components/homev3/parchmentCard";
import { resolveBook } from "../lib/bookLookup";
import { useAutosave } from "../lib/useAutosave";
import { translatingLine } from "../lib/translatingLine";

// ─────────────────────────────────────────────────────────────────────
//  Constants
// ─────────────────────────────────────────────────────────────────────

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/mycraftz-install`;

// The backend writes a skeleton record within ~1s of the scaffold call, so
// don't block on the (slow, LLM-filled) scaffold response — cap the wait.
const waitForCraftprint = (ready) =>
  Promise.race([ready, new Promise((r) => setTimeout(r, 2000))]);

// Retries a write that 404s while the CraftPrint record is still landing.
async function withNotFoundRetry(fn, delays = [1500, 3000, 5000]) {
  for (let i = 0; ; i++) {
    try {
      return await fn();
    } catch (e) {
      if (e?.response?.status !== 404 || i >= delays.length) throw e;
      await new Promise((r) => setTimeout(r, delays[i]));
    }
  }
}
const LS_SANKALPA = "icraftz.sankalpa.commit.v1";
const RULE_OF_N = 3;
const AXIOS_TIMEOUT_MS = 60000;

// ─────────────────────────────────────────────────────────────────────
//  Sankalpa-commit read helper — READ-ONLY, ~3 lines (per BUILD SPEC §2)
//  Sadhana never writes to any localStorage key.
// ─────────────────────────────────────────────────────────────────────

function readSankalpaCommit(bookId) {
  if (typeof window === "undefined") return { committed: [], committedAt: null };
  try {
    const raw = window.localStorage.getItem(LS_SANKALPA);
    const all = raw ? JSON.parse(raw) : {};
    const entry = (all && typeof all === "object" && all[String(bookId)]) || {};
    return {
      committed:   Array.isArray(entry.committed) ? entry.committed : [],
      committedAt: typeof entry.committedAt === "string" ? entry.committedAt : null,
    };
  } catch {
    return { committed: [], committedAt: null };
  }
}

// ── Bridge seed (?bridge=1 from /mycraftz) ──────────────────────────────
// Logged-in users arriving from the old /mycraftz installed grid / "Continue
// Crafting" ribbon have no local Sankalpa commit for this book. To land them
// on the spec-compliant Sadhana without a bigger migration, seed a commit of
// the book's first three ideas so the normal scaffold path runs (owner-scoped
// via authHeaders). This is the ONLY write Sadhana ever makes to LS.
const BRIDGE_COMMIT_IDS = [1, 2, 3];
function seedSankalpaCommit(bookId, committed) {
  if (typeof window === "undefined") return;
  try {
    const raw = window.localStorage.getItem(LS_SANKALPA);
    const all = raw && typeof JSON.parse(raw) === "object" ? JSON.parse(raw) : {};
    all[String(bookId)] = { committed, committedAt: new Date().toISOString() };
    window.localStorage.setItem(LS_SANKALPA, JSON.stringify(all));
  } catch {
    /* quota / private-mode — bridge simply won't persist, page shows needs-sankalpa */
  }
}

function formatCommittedDate(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

// ─────────────────────────────────────────────────────────────────────
//  Presentational primitives — diya + skeleton (matched to Bodha/Sankalpa)
// ─────────────────────────────────────────────────────────────────────

function Diya() {
  return (
    <div
      aria-hidden="true"
      style={{ display: "inline-flex", alignItems: "flex-end", gap: 6, verticalAlign: "middle" }}
    >
      <span
        style={{
          display: "inline-block",
          width: 10,
          height: 14,
          borderRadius: "50% 50% 45% 45% / 60% 60% 40% 40%",
          background: "radial-gradient(circle at 50% 35%, #FFE9A6 0%, #E4A82C 55%, #8B5A22 100%)",
          animation: "sadhana-flicker 1.6s ease-in-out infinite alternate",
          transformOrigin: "50% 100%",
        }}
      />
      <span
        style={{
          display: "inline-block",
          width: 26,
          height: 6,
          borderRadius: "50%",
          background: "linear-gradient(180deg, #6b3d1a 0%, #3a1e0c 100%)",
          boxShadow: "inset 0 -1px 0 rgba(0,0,0,0.35)",
        }}
      />
      <style>{`
        @keyframes sadhana-flicker {
          0%   { transform: scale(1)   translateY(0);    filter: brightness(1); }
          50%  { transform: scale(1.06) translateY(-1px); filter: brightness(1.12); }
          100% { transform: scale(0.98) translateY(1px);  filter: brightness(0.94); }
        }
        @keyframes sadhana-shimmer {
          0%   { background-position: -200px 0; }
          100% { background-position: 200px 0; }
        }
        @keyframes sadhana-rise {
          0%   { opacity: 0; transform: translateY(4px); }
          25%  { opacity: 1; }
          100% { opacity: 0; transform: translateY(-16px); }
        }
        @keyframes sadhana-fadein {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: none; }
        }
      `}</style>
    </div>
  );
}

function skelBar(widthPct, height, mb) {
  return {
    width: `${widthPct}%`,
    height,
    marginBottom: mb,
    borderRadius: 4,
    background:
      "linear-gradient(90deg, rgba(139,99,71,0.08) 0%, rgba(139,99,71,0.18) 50%, rgba(139,99,71,0.08) 100%)",
    backgroundSize: "400px 100%",
    animation: "sadhana-shimmer 1.4s ease-in-out infinite",
  };
}

// ─────────────────────────────────────────────────────────────────────
//  Sadhana card fields — Sutra + a labelled Nimitta block + a separate Kriya
//  line + Kala-on-tap, a
//  per-card Mark-done / streak row (which reveals the read-only Phala as a
//  completion flourish), and a Vighna→Upaya coping plan with curated preset
//  chips. Lakshya and Phala are NOT shown on the resting card — Lakshya
//  belongs to Siddhi; Phala surfaces only as the Mark-done flourish and is
//  AI-authored / read-only. No progressive-reveal gating.
// ─────────────────────────────────────────────────────────────────────

function StaticField({ label, value }) {
  if (!value) return null;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontFamily: T.F_HEADING, fontSize: 10, letterSpacing: "0.16em", color: T.GOLD, textTransform: "uppercase" }}>{label}</span>
      <p style={{ margin: 0, fontFamily: T.F_BODY, fontSize: 14, lineHeight: 1.55, color: T.DARK_BROWN }}>{value}</p>
    </div>
  );
}

function KalaToggle({ cardId, kala }) {
  const [open, setOpen] = React.useState(false);
  if (!kala) return null;
  return (
    <div style={{ marginTop: 4 }}>
      <button
        type="button"
        data-testid={`sadhana-kala-toggle-${cardId}`}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "inline-flex", alignItems: "center", gap: 5, background: "none",
          border: 0, padding: 0, cursor: "pointer", color: T.MUTED_BROWN,
          fontFamily: T.F_BODY, fontStyle: "italic", fontSize: 12,
        }}
      >
        {open ? "Hide time" : "How long? (Kala)"}
        <ChevronDown size={13} strokeWidth={1.75} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 160ms ease" }} />
      </button>
      {open && (
        <p
          data-testid={`sadhana-kala-value-${cardId}`}
          className="m-0"
          style={{ marginTop: 4, fontFamily: T.F_BODY, fontSize: 13, color: T.DARK_BROWN }}
        >
          ~ {kala}
        </p>
      )}
    </div>
  );
}

// ── Completion row — per-card mark-done + streak + X-of-30 kept ──
// Non-punitive: gold flame + kept-count only. No red, no "broken streak",
// no countdown. Marking done reveals the (read-only) Phala — "what's
// arriving" — as a gentle completion flourish. Phala is AI-authored and
// is never shown on the resting card and never user-editable.
function CompletionRow({ cardId, summary, phala, onToggle }) {
  const daysKept = summary?.days_kept || 0;
  const doneToday = !!summary?.done_today;
  const pending = !!summary?.pending;
  const saveError = summary?.error;
  const target = summary?.target || SADHANA_TARGET_DAYS;
  const [flourish, setFlourish] = React.useState(false);

  const toggle = async () => {
    if (pending) return;
    const next = !doneToday;
    const ok = await onToggle(next);
    if (!ok) return;
    if (next) {
      setFlourish(true);
      window.setTimeout(() => setFlourish(false), phala ? 5200 : 1200);
    } else {
      setFlourish(false);
    }
  };

  return (
    <div
      style={{
        display: "flex", flexDirection: "column", gap: 12, marginTop: 4,
        paddingTop: 14, borderTop: "1px solid rgba(201,168,76,0.28)",
      }}
    >
      <div
        data-testid={`sadhana-completion-${cardId}`}
        style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          gap: 12, flexWrap: "wrap",
        }}
      >
        <div style={{ display: "inline-flex", alignItems: "center", gap: 8, minWidth: 0 }}>
          <Flame size={16} strokeWidth={1.75} color={daysKept > 0 ? T.GOLD : "rgba(139,99,71,0.45)"} />
          <span
            data-testid={`sadhana-streak-${cardId}`}
            style={{ fontFamily: T.F_HEADING, fontSize: 13, letterSpacing: "0.04em", color: T.DARK_BROWN }}
          >
            {daysKept} of {target} days kept
          </span>
        </div>

        <div style={{ position: "relative" }}>
          <button
            type="button"
            data-testid={`sadhana-markdone-${cardId}`}
            onClick={toggle}
            disabled={pending}
            aria-busy={pending}
            aria-pressed={doneToday}
            style={{
              display: "inline-flex", alignItems: "center", gap: 7,
              padding: "8px 18px", borderRadius: 999, cursor: pending ? "wait" : "pointer",
              opacity: pending ? 0.7 : 1,
              fontFamily: T.F_HEADING, fontSize: 12, letterSpacing: "0.08em",
              border: `1px solid ${T.GOLD}`,
              background: doneToday ? T.GOLD : "transparent",
              color: doneToday ? "#fff" : T.DARK_BROWN,
              transition: "background 160ms ease, color 160ms ease",
            }}
          >
            {doneToday ? <Check size={14} strokeWidth={2.25} /> : <Flame size={13} strokeWidth={1.75} />}
            {pending ? "Saving…" : doneToday ? "Kept today" : "Mark done today"}
          </button>
          {flourish && !phala && (
            <span
              aria-hidden="true"
              data-testid={`sadhana-flourish-${cardId}`}
              style={{
                position: "absolute", right: 6, top: -18, fontFamily: T.F_HEADING,
                fontSize: 12, color: T.GOLD, pointerEvents: "none",
                animation: "sadhana-rise 1.2s ease-out forwards",
              }}
            >
              ✦ kept
            </span>
          )}
        </div>
      </div>

      {saveError && (
        <p
          role="alert"
          data-testid={`sadhana-markdone-error-${cardId}`}
          className="m-0"
          style={{ fontFamily: T.F_BODY, fontSize: 13, color: "#8B3A2E", fontStyle: "italic" }}
        >
          {saveError}
        </p>
      )}

      {flourish && phala && (
        <div
          data-testid={`sadhana-phala-flourish-${cardId}`}
          style={{
            background: "rgba(201,168,76,0.1)",
            border: "1px solid rgba(201,168,76,0.35)",
            borderRadius: 8,
            padding: "12px 14px",
            animation: "sadhana-fadein 400ms ease-out",
          }}
        >
          <span style={{ fontFamily: T.F_HEADING, fontSize: 10, letterSpacing: "0.16em", color: T.GOLD, textTransform: "uppercase" }}>
            ✦ What&apos;s arriving · Phala
          </span>
          <p className="m-0" style={{ marginTop: 4, fontFamily: T.F_BODY, fontStyle: "italic", fontSize: 14, lineHeight: 1.55, color: T.DARK_BROWN }}>
            {phala}
          </p>
        </div>
      )}
    </div>
  );
}

// ── Coping plan — Vighna (obstacle) → Upaya (antidote) with curated chips ──
function CopingPlan({ cardId, vighna, upaya, onPickUpaya }) {
  const [open, setOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [saveError, setSaveError] = React.useState("");

  const pick = async (chip) => {
    setSaving(true);
    setSaveError("");
    try {
      const ok = await onPickUpaya(chip);
      if (!ok) setSaveError("Couldn't save your coping plan. Please try again.");
    } finally { setSaving(false); }
  };

  return (
    <div
      data-testid={`sadhana-coping-${cardId}`}
      style={{ marginTop: 2, paddingTop: 14, borderTop: "1px dashed rgba(201,168,76,0.5)" }}
    >
      <button
        type="button"
        data-testid={`sadhana-coping-toggle-${cardId}`}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "inline-flex", alignItems: "center", gap: 7, background: "none",
          border: 0, padding: 0, cursor: "pointer", color: T.DARK_BROWN,
          fontFamily: T.F_HEADING, fontSize: 12, letterSpacing: "0.08em",
        }}
      >
        <LifeBuoy size={14} strokeWidth={1.75} color={T.GOLD} />
        {open ? "Hide coping plan" : "Facing an obstacle? Make a coping plan"}
        <ChevronDown size={13} strokeWidth={1.75} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform 160ms ease" }} />
      </button>

      {open && (
        <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 12 }}>
          {vighna && (
            <div>
              <span style={{ fontFamily: T.F_HEADING, fontSize: 10, letterSpacing: "0.16em", color: T.GOLD, textTransform: "uppercase" }}>
                Obstacle · Vighna
              </span>
              <p className="m-0" style={{ marginTop: 2, fontFamily: T.F_BODY, fontSize: 14, lineHeight: 1.55, color: T.DARK_BROWN }}>
                {vighna}
              </p>
            </div>
          )}
          <div>
            <span style={{ fontFamily: T.F_HEADING, fontSize: 10, letterSpacing: "0.16em", color: T.GOLD, textTransform: "uppercase" }}>
              Your antidote · Upaya
            </span>
            {upaya ? (
              <p
                data-testid={`sadhana-upaya-value-${cardId}`}
                className="m-0"
                style={{ marginTop: 2, marginBottom: 10, fontFamily: T.F_BODY, fontSize: 14, lineHeight: 1.55, color: T.DARK_BROWN, fontStyle: "italic" }}
              >
                “{upaya}”
              </p>
            ) : (
              <p className="m-0" style={{ marginTop: 2, marginBottom: 10, fontFamily: T.F_BODY, fontSize: 13, color: T.MUTED_BROWN, fontStyle: "italic" }}>
                Tap one to set your remedy — it becomes your plan when the obstacle appears.
              </p>
            )}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {UPAYA_PRESET_CHIPS.map((chip, i) => {
                const active = chip === upaya;
                return (
                  <button
                    key={i}
                    type="button"
                    data-testid={`sadhana-upaya-chip-${cardId}-${i}`}
                    onClick={() => pick(chip)}
                    disabled={saving}
                    aria-pressed={active}
                    style={{
                      padding: "6px 12px", borderRadius: 999, cursor: saving ? "wait" : "pointer",
                      fontFamily: T.F_BODY, fontSize: 12, lineHeight: 1.3,
                      border: `1px solid ${active ? T.GOLD : "rgba(201,168,76,0.5)"}`,
                      background: active ? "rgba(201,168,76,0.22)" : "rgba(255,255,255,0.4)",
                      color: T.DARK_BROWN, transition: "background 140ms ease, border-color 140ms ease",
                    }}
                  >
                    {chip}
                  </button>
                );
              })}
            </div>
            {saveError && (
              <p
                role="alert"
                data-testid={`sadhana-upaya-error-${cardId}`}
                className="m-0"
                style={{ marginTop: 8, fontFamily: T.F_BODY, fontSize: 13, color: "#8B3A2E", fontStyle: "italic" }}
              >
                {saveError}
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Phala / Vighna / Upaya — the demo's labelled field look (dcp-field), with
// real autosave. Editable from day 1 so Siddhi is never blocked.
const REFLECTION_FIELDS = [
  { key: "phala",  label: "Phala",  hint: "Fruit — what's arriving" },
  { key: "vighna", label: "Vighna", hint: "Obstacle — what's in the way" },
  { key: "upaya",  label: "Upaya",  hint: "Remedy — a small next step" },
];

function ReflectionField({ cardId, field, label, hint, value, edited, suggestion, onSave }) {
  const [draft, setDraft] = React.useState(edited ? value || "" : "");
  React.useEffect(() => { if (edited) setDraft(value || ""); }, [value, edited]);
  const { status, dirty, flush } = useAutosave({ draft, savedValue: edited ? value : "", onSave });
  const showSaved = edited && !dirty && status !== "saving" && status !== "error";
  const inputId = `sadhana-${field}-${cardId}`;

  return (
    <div className="dcp-field" data-testid={`sadhana-${field}-field-${cardId}`}>
      <div className="dcp-field-head">
        <label className="dcp-field-label" htmlFor={inputId}>{label}</label>
        <span className="dcp-field-hint">{hint}</span>
        <span className="sad-field-status" aria-live="polite">
          {status === "saving" && <span data-testid={`sadhana-${field}-saving-${cardId}`}>Saving…</span>}
          {showSaved && <span className="is-saved" data-testid={`sadhana-${field}-saved-${cardId}`}>Saved ✓</span>}
        </span>
      </div>
      <textarea
        id={inputId}
        className="dcp-field-textarea"
        data-testid={`sadhana-${field}-input-${cardId}`}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={flush}
        placeholder={suggestion ? `e.g. ${suggestion}` : "In your own words…"}
        rows={2}
        maxLength={2000}
      />
      <CharCount value={draft} max={2000} testId={`sadhana-${field}-count-${cardId}`} />
      {(dirty || status === "error") && (
        <div className="dcp-field-actions">
          <button type="button" className="dcp-btn-save" data-testid={`sadhana-${field}-save-${cardId}`} onClick={flush} disabled={status === "saving"}>
            {status === "error" ? "Retry" : "Save"}
          </button>
          {status === "error" && (
            <span role="alert" className="sad-field-error" data-testid={`sadhana-${field}-error-${cardId}`}>
              Couldn&apos;t save. Please try again.
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function ReflectionBlock({ cardId, cp, onEdit }) {
  return (
    <div className="dcp-tier3" data-testid={`sadhana-reflections-${cardId}`}>
      {REFLECTION_FIELDS.map(({ key, label, hint }) => (
        <ReflectionField
          key={key} cardId={cardId} field={key} label={label} hint={hint}
          value={cp[key]} edited={!!cp[`${key}_edited_at`]} suggestion={cp[key]}
          onSave={(v) => onEdit(key, v)}
        />
      ))}
    </div>
  );
}

function PracticeCard({ idea, craftprint, index, isSkeleton, completion, onToggleComplete, onEdit, bookTitle, bookCover, day = 0 }) {
  const [uiLang] = useLanguage();
  const cardStyle = {
    position: "relative", background: CARD_BG, border: CARD_BORDER,
    borderRadius: CARD_RADIUS, padding: "24px 28px", boxShadow: CARD_SHADOW,
    minWidth: 0, display: "flex", flexDirection: "column", gap: 16,
  };

  if (isSkeleton) {
    return (
      <div style={cardStyle} aria-hidden="true">
        <div style={skelBar(40, 14, 8)} />
        <div style={skelBar(65, 22, 10)} />
        <div style={skelBar(100, 13, 4)} />
        <div style={skelBar(85, 13, 0)} />
      </div>
    );
  }

  const cp = craftprint || {};
  const title = (cp.title || idea?.title || "");
  const sutra = (cp.sutra || idea?.description || "");
  const ideaId = cp.idea_id ?? idea?.id;
  const nimitta = (cp.nimitta || "").trim();
  const kriya = (cp.kriya || "").trim();

  const kala = (cp.kala || "").trim();
  const tier = revealTierForDays(day);

  return (
    <div className="mc-page mc-embed">
      <article className="mc-active-card dcp-card" data-testid={`sadhana-practice-${ideaId}`}>
        <span className="dcp-demo-ribbon" data-testid={`sadhana-ribbon-${ideaId}`}>
          Practice {String(index + 1).padStart(2, "0")} · Day {day + 1} of 30
        </span>
        <header className="mc-active-head">
          {bookCover && (
            <img src={bookCover} alt={`${bookTitle} cover`} className="mc-active-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          )}
          <div className="mc-active-head-text">
            <p className="mc-active-eyebrow">From <em>{bookTitle}</em></p>
            <h3 className="mc-active-title">{title}</h3>
            {sutra && <p className="mc-active-sutra" data-testid={`sadhana-sutra-${ideaId}`}>&ldquo;{sutra}&rdquo;</p>}
          </div>
        </header>

        <dl className="mc-sutra-grid dcp-tier1-grid">
          <div>
            <dt><Feather size={13} /> Kriya</dt>
            <dd data-testid={`sadhana-kriya-${ideaId}`}>{kriya || (uiLang === "en" ? "Preparing your practice…" : translatingLine(uiLang))}</dd>
          </div>
        </dl>

        {tier >= 2 ? (
          <dl className="mc-sutra-grid dcp-tier2-grid" data-testid={`sadhana-tier2-${ideaId}`}>
            <div>
              <dt><Calendar size={13} /> Nimitta</dt>
              <dd data-testid={`sadhana-nimitta-${ideaId}`}>{nimitta || "—"}</dd>
            </div>
            <div>
              <dt><Hourglass size={13} /> Kala</dt>
              <dd data-testid={`sadhana-kala-${ideaId}`}>{kala || "—"}</dd>
            </div>
          </dl>
        ) : (
          <div className="dcp-reveal-banner" data-testid={`sadhana-reveal-day-3-${ideaId}`}>
            <Sparkles size={11} strokeWidth={1.6} />
            <span>Nimitta and Kala appear on day 3 of your practice.</span>
          </div>
        )}

        <CompletionRow
          cardId={ideaId}
          summary={completion}
          phala={cp.phala}
          onToggle={(next) => onToggleComplete(ideaId, next)}
        />

        <ReflectionBlock cardId={ideaId} cp={cp} onEdit={onEdit} />

        <CopingPlan
          cardId={ideaId}
          vighna={cp.vighna}
          upaya={cp.upaya}
          onPickUpaya={(chip) => onEdit("upaya", chip)}
        />

        <footer className="dcp-foot">
          <span className="dcp-day-badge" data-testid={`sadhana-day-${ideaId}`}>
            <ScrollText size={11} /> Day {day + 1}
          </span>
        </footer>
      </article>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Left panel — Current Book (display-only variant)
// ─────────────────────────────────────────────────────────────────────

function CurrentBookPanel({ book, committedAt }) {
  return (
    <aside
      data-testid="sadhana-current-book"
      className="sadhana-current-book"
      style={{
        background: CARD_BG,
        border: CARD_BORDER,
        borderRadius: CARD_RADIUS,
        padding: 20,
        boxShadow: CARD_SHADOW,
        position: "sticky",
        top: 84,
        maxHeight: "calc(100vh - 108px)",
        minWidth: 0,
      }}
    >
      <p
        className="m-0"
        style={{
          fontFamily: T.F_HEADING,
          fontSize: 11,
          letterSpacing: "0.16em",
          color: T.GOLD,
          textAlign: "center",
          marginBottom: 14,
        }}
      >
        CURRENT BOOK
      </p>

      <div
        style={{
          aspectRatio: "3/4",
          background: "#EADFC4",
          overflow: "hidden",
          borderRadius: 6,
          border: `1px solid ${T.GOLD}`,
          marginBottom: 14,
        }}
      >
        <img
          src={book.coverUrl || book.image}
          alt={book.title}
          style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
        />
      </div>

      <h2
        className="m-0"
        style={{
          fontFamily: T.F_HEADING,
          fontSize: 18,
          fontWeight: 700,
          color: T.DARK_BROWN,
          lineHeight: 1.2,
          textAlign: "center",
          marginBottom: 4,
        }}
      >
        {book.title}
      </h2>
      <p
        className="m-0"
        style={{
          fontFamily: T.F_BODY,
          fontStyle: "italic",
          fontSize: 13,
          color: T.MUTED_BROWN,
          textAlign: "center",
          marginBottom: 14,
        }}
      >
        {book.author}
      </p>

      <div style={{ display: "flex", justifyContent: "center", marginBottom: 14 }}>
        <GoldRule />
      </div>

      <p
        className="m-0"
        style={{
          fontFamily: T.F_BODY,
          fontSize: 13,
          color: T.DARK_BROWN,
          textAlign: "center",
          marginBottom: 6,
        }}
      >
        <strong>{RULE_OF_N}</strong> daily practices
      </p>
      {committedAt && (
        <p
          className="m-0"
          style={{
            fontFamily: T.F_BODY,
            fontStyle: "italic",
            fontSize: 11,
            color: T.MUTED_BROWN,
            textAlign: "center",
            marginBottom: 16,
          }}
        >
          Committed {formatCommittedDate(committedAt)}
        </p>
      )}

      <Link
        to={R.library}
        data-testid="sadhana-change-book"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          width: "100%",
          padding: "10px 12px",
          fontFamily: T.F_HEADING,
          fontSize: 12,
          letterSpacing: "0.08em",
          color: T.DARK_BROWN,
          textDecoration: "none",
          borderRadius: 999,
          border: `1px solid ${T.GOLD}`,
          background: "transparent",
          transition: "background 140ms ease",
          marginBottom: 10,
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(201,168,76,0.15)")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      >
        Change book <ArrowRight size={12} strokeWidth={1.75} />
      </Link>

      <div style={{ textAlign: "center" }}>
        <Link
          to={`/sankalpa?book=${book.id}`}
          data-testid="sadhana-reseal-link"
          style={{
            fontFamily: T.F_BODY,
            fontStyle: "italic",
            fontSize: 11,
            color: T.MUTED_BROWN,
            textDecoration: "underline",
            textDecorationColor: T.GOLD,
            textUnderlineOffset: 3,
          }}
        >
          Re-seal your Sankalpa
        </Link>
      </div>
    </aside>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  State (b) — Book present but no Sankalpa commit yet
// ─────────────────────────────────────────────────────────────────────

function NeedsSankalpa({ book }) {
  return (
    <div
      data-testid="sadhana-needs-sankalpa"
      style={{ maxWidth: 640, margin: "0 auto", textAlign: "center", padding: "70px 24px" }}
    >
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 18 }}>
        <GoldRule wide />
      </div>
      <StageKicker stage="sadhana" text="SADHANA · साधना" size="large" testId="sadhana-needs-kicker" />
      <h1
        className="m-0 sadhana-empty-h1"
        style={{
          fontFamily: T.F_DISPLAY,
          fontSize: 42,
          fontWeight: 700,
          color: T.DARK_BROWN,
          lineHeight: 1.1,
          marginBottom: 14,
        }}
      >
        Sadhana needs a Sankalpa.
      </h1>
      <p
        className="m-0"
        style={{
          fontFamily: T.F_BODY,
          fontStyle: "italic",
          fontSize: 17,
          color: T.MUTED_BROWN,
          lineHeight: 1.55,
          marginBottom: 28,
        }}
      >
        Choose three ideas from <strong>{book.title}</strong> first — that is
        what you will practice.
      </p>
      <div
        style={{
          display: "flex",
          gap: 12,
          justifyContent: "center",
          flexWrap: "wrap",
        }}
      >
        <Link
          to={`/sankalpa?book=${book.id}`}
          data-testid="sadhana-goto-sankalpa"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "12px 26px",
            fontFamily: T.F_HEADING,
            fontSize: 13,
            letterSpacing: "0.10em",
            color: T.PARCHMENT_LT,
            textDecoration: "none",
            borderRadius: 999,
            background: T.DARK_BROWN,
            border: `1px solid ${T.DARK_BROWN}`,
            boxShadow: "0 3px 10px rgba(44,24,16,0.20)",
          }}
        >
          Make your Sankalpa <ArrowRight size={14} strokeWidth={1.75} />
        </Link>
        <Link
          to={`/bodha?book=${book.id}`}
          data-testid="sadhana-goto-bodha"
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: "12px 26px",
            fontFamily: T.F_HEADING,
            fontSize: 13,
            letterSpacing: "0.10em",
            color: T.DARK_BROWN,
            textDecoration: "none",
            borderRadius: 999,
            background: "transparent",
            border: `1px solid ${T.GOLD}`,
          }}
        >
          <BookOpen size={14} strokeWidth={1.75} /> Read the ideas first (Bodha)
        </Link>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  State (c) — No book param at all
// ─────────────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div
      data-testid="sadhana-empty-state"
      style={{ maxWidth: 620, margin: "0 auto", textAlign: "center", padding: "80px 24px" }}
    >
      <div style={{ display: "flex", justifyContent: "center", marginBottom: 18 }}>
        <GoldRule wide />
      </div>
      <StageKicker stage="sadhana" text="SADHANA · साधना" size="large" testId="sadhana-empty-kicker" />
      <h1
        className="m-0 sadhana-empty-h1"
        style={{
          fontFamily: T.F_DISPLAY,
          fontSize: 44,
          fontWeight: 700,
          color: T.DARK_BROWN,
          lineHeight: 1.1,
          marginBottom: 14,
        }}
      >
        Choose a book to begin your practice.
      </h1>
      <p
        className="m-0"
        style={{
          fontFamily: T.F_BODY,
          fontStyle: "italic",
          fontSize: 17,
          color: T.MUTED_BROWN,
          lineHeight: 1.55,
          marginBottom: 26,
        }}
      >
        Sadhana is where the three ideas you committed to become your daily
        practice. Pick any book from the Library to begin.
      </p>
      <Link
        to={R.library}
        data-testid="sadhana-empty-cta"
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "12px 26px",
          fontFamily: T.F_HEADING,
          fontSize: 13,
          letterSpacing: "0.10em",
          color: T.PARCHMENT_LT,
          textDecoration: "none",
          borderRadius: 999,
          background: T.DARK_BROWN,
          border: `1px solid ${T.DARK_BROWN}`,
          boxShadow: "0 3px 10px rgba(44,24,16,0.20)",
        }}
      >
        Explore the Library <ArrowRight size={14} strokeWidth={1.75} />
      </Link>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Error + Loading + Small pieces
// ─────────────────────────────────────────────────────────────────────

function ErrorState({ bookTitle, onRetry }) {
  return (
    <div
      data-testid="sadhana-error-state"
      style={{
        maxWidth: 520,
        margin: "60px auto",
        textAlign: "center",
        padding: "40px 24px",
        background: CARD_BG,
        border: CARD_BORDER,
        borderRadius: CARD_RADIUS,
        boxShadow: CARD_SHADOW,
      }}
    >
      <p
        className="m-0"
        style={{
          fontFamily: T.F_HEADING,
          fontSize: 11,
          letterSpacing: "0.18em",
          color: "#8B3A12",
          marginBottom: 10,
        }}
      >
        UNABLE TO LOAD PRACTICE
      </p>
      <p
        className="m-0"
        style={{
          fontFamily: T.F_BODY,
          fontStyle: "italic",
          fontSize: 15,
          color: T.MUTED_BROWN,
          marginBottom: 22,
        }}
      >
        The oracle went quiet while retrieving {bookTitle}. Try again in a moment.
      </p>
      <button
        type="button"
        data-testid="sadhana-retry"
        onClick={onRetry}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 8,
          padding: "10px 22px",
          fontFamily: T.F_HEADING,
          fontSize: 12,
          letterSpacing: "0.10em",
          color: T.PARCHMENT_LT,
          background: T.DARK_BROWN,
          border: `1px solid ${T.DARK_BROWN}`,
          borderRadius: 999,
          cursor: "pointer",
        }}
      >
        <RefreshCw size={13} strokeWidth={1.75} /> Try again
      </button>
    </div>
  );
}

function LoadingBanner({ bookTitle }) {
  return (
    <div
      data-testid="sadhana-loading-banner"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        margin: "0 auto 22px",
        padding: "10px 22px",
        maxWidth: 620,
        background: "rgba(240,230,204,0.6)",
        border: `1px dashed ${T.GOLD}`,
        borderRadius: 999,
      }}
    >
      <Diya />
      <p
        className="m-0"
        style={{
          fontFamily: T.F_BODY,
          fontStyle: "italic",
          fontSize: 14,
          color: T.DARK_BROWN,
        }}
      >
        Retrieving your practice from <strong>{bookTitle}</strong>…
      </p>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
//  Main page
// ─────────────────────────────────────────────────────────────────────

export default function SadhanaPage() {
  const [params] = useSearchParams();
  const bookParam = params.get("book");
  const bridge = params.get("bridge") === "1";
  const book = useMemo(() => resolveBook(bookParam), [bookParam]);
  const { isAuthenticated, authHeaders, user } = useAuth();

  // Fresh read of commit state on EVERY book change (book-switching guard)
  const [commit, setCommit] = useState({ committed: [], committedAt: null });
  const [commitChecking, setCommitChecking] = useState(false);

  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // ── Language-aware artefact translation ──────────────────────────
  // Sadhana renders the 3 committed ideas as daily practices with their
  // title + description. When a non-EN language is active, we need those
  // fields translated so users who committed in (e.g.) Gujarati on
  // Sankalpa see the same phrasing on Sadhana. Ids stay stable — the
  // committed[] filter still works untouched. Mirrors the BodhaPage +
  // SankalpaPage pattern surfaced by scripts/audit_routes.py.
  const [lang] = useLanguage();
  const [translatedIdeas, setTranslatedIdeas] = useState(null);
  const [isTranslatingIdeas, setIsTranslatingIdeas] = useState(false);
  const translateCacheRef = useRef({}); // { `${bookId}::${lang}` → ideas[] }

  useEffect(() => {
    if (!book) {
      setCommit({ committed: [], committedAt: null });
      return;
    }
    // ALWAYS re-read from LS on every book change. Never carry component-lifetime state.
    let existing = readSankalpaCommit(book.id);
    // Bridge (?bridge=1): arriving from /mycraftz (installed grid OR showcase
    // "Accept" preview) with no real commit → seed first 3 ideas so the spec
    // Sadhana renders. Works for anonymous preview visitors too; never
    // overwrites an existing full 3-idea commit.
    if (bridge && existing.committed.length !== RULE_OF_N) {
      seedSankalpaCommit(book.id, BRIDGE_COMMIT_IDS);
      existing = readSankalpaCommit(book.id);
    }
    setCommit(existing);
    if (existing.committed.length === RULE_OF_N || !isAuthenticated) return;
    // New device / cleared storage: recover the seal from the server.
    let cancelled = false;
    setCommitChecking(true);
    (async () => {
      try {
        const base = `${process.env.REACT_APP_BACKEND_URL}/api/mycraftz-install`;
        const r = await axios.get(`${base}/sankalpa/seal`, {
          params: { book_id: book.id, targetLanguage: lang }, headers: authHeaders(),
        });
        let ids = r.data?.found ? r.data.idea_ids : null;
        if (!ids) {
          const g = await axios.get(`${base}/craftprints`, {
            params: { book_id: book.id, targetLanguage: lang }, headers: authHeaders(),
          });
          if (g.data?.found) ids = (g.data.craftprints || []).map((c) => Number(c.idea_id));
        }
        if (!cancelled && ids && ids.length === RULE_OF_N) {
          seedSankalpaCommit(book.id, ids);
          setCommit(readSankalpaCommit(book.id));
        }
      } catch (e) {
        // fall through to the NeedsSankalpa prompt
      } finally {
        if (!cancelled) setCommitChecking(false);
      }
    })();
    return () => { cancelled = true; };
  }, [book, bridge, isAuthenticated, lang, authHeaders]);

  const hasCommit = commit.committed.length === RULE_OF_N;

  const fetchIdeas = useCallback(async () => {
    if (!book || !hasCommit) return;
    setLoading(true);
    setError(null);
    setIdeas([]);
    const snapshot = {
      id:          String(book.id),
      title:       book.title || "",
      author:      book.author || "",
      summary:     book.summary || "",
      insights:    Array.isArray(book.insights)    ? book.insights    : [],
      actionItems: Array.isArray(book.actionItems) ? book.actionItems : [],
      coreTheme:   book.coreTheme || "",
    };
    try {
      const res = await axios.post(
        `${API_BASE}/ideas`,
        { book: snapshot },
        { timeout: AXIOS_TIMEOUT_MS }
      );
      const list = Array.isArray(res.data?.ideas) ? res.data.ideas : [];
      setIdeas(list);
    } catch (e) {
      setError(true);
    } finally {
      setLoading(false);
    }
  }, [book, hasCommit]);

  // ── CraftPrints (8-field per idea) ───────────────────────────────
  // On mount, GET the persisted install for this (owner, book, lang).
  // If it doesn't exist yet (first Sadhana arrival), POST scaffold to
  // build it. Subsequent visits are pure GET reads.
  const [craftprints, setCraftprints] = useState([]);
  const [daysSinceInstall, setDaysSinceInstall] = useState(0);
  const [craftprintLoading, setCraftprintLoading] = useState(false);
  // Per-card daily completion summary: { "<ideaId>": {days_kept, done_today, target} }
  const [completions, setCompletions] = useState({});
  // Resolved backend install_id for the current (owner, book, lang) — used
  // by mark-done + patch for logged-in users.
  const [craftprintInstallId, setCraftprintInstallId] = useState(null);
  // Resolves once the backend CraftPrint record exists. Mark-done / Upaya
  // await this so a click right after sealing never races the scaffold.
  const craftprintReadyRef = useRef(Promise.resolve());

  useEffect(() => {
    if (!book || !hasCommit || commit.committed.length === 0) return;
    let cancelled = false;
    craftprintReadyRef.current = (async () => {
      setCraftprintLoading(true);
      try {
        // Read first — fast path for returning users.
        const g = await axios.get(`${API_BASE}/craftprints`, {
          params: { book_id: String(book.id), targetLanguage: lang },
          headers: authHeaders(),
          timeout: AXIOS_TIMEOUT_MS,
        });
        if (!cancelled && g.data?.found) {
          setCraftprints(g.data.craftprints || []);
          setDaysSinceInstall(Number(g.data.days_since_install || 0));
          setCraftprintInstallId(`${g.data.owner_id || "anon"}:${book.id}:${lang}`);
          if (isAuthenticated) setCompletions(g.data.completions || {});
          // An unfinished scaffold (skeleton) falls through to finish it.
          // Same journey in a new language: scaffold only fills the translated display text.
          if (!g.data.scaffolding && !g.data.needs_translation) return;
        }
        // Not found → scaffold with the committed ideas.
        const s = await withTransientRetry(() =>
          axios.post(
            `${API_BASE}/craftprint/scaffold`,
            {
              book: {
                id: String(book.id), title: book.title, author: book.author,
                summary: book.summary || "", insights: book.insights || [],
                actionItems: book.actionItems || [], coreTheme: book.coreTheme || "",
              },
              committed_idea_ids: commit.committed,
              targetLanguage: lang,
            },
            { headers: authHeaders(), timeout: AXIOS_TIMEOUT_MS }
          )
        );
        if (!cancelled) {
          setCraftprints(s.data?.craftprints || []);
          setCraftprintInstallId(s.data?.install_id || `${s.data?.owner_id || "anon"}:${book.id}:${lang}`);
        }
      } catch (e) {
      } finally {
        if (!cancelled) setCraftprintLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [book, hasCommit, commit.committed, lang, isAuthenticated, authHeaders]);

  // Anonymous / preview users track completions locally (localStorage) —
  // the shared "anon" backend install_id would otherwise collide across
  // every guest. Logged-in users are backend-driven (set above).
  useEffect(() => {
    if (!book || !hasCommit || isAuthenticated) return;
    if (!commit.committed.length) return;
    setCompletions(readAnonCompletions(book.id, lang, commit.committed));
  }, [book, hasCommit, isAuthenticated, lang, commit.committed]);

  const handleToggleComplete = useCallback(async (ideaId, nextDone) => {
    if (!book) return;
    // Logged-in users ALWAYS act on their real owner-scoped install id —
    // never the shared "anon" key (which 404s and silently drops writes).
    const ownerScopedId = (isAuthenticated && user?.id)
      ? `${user.id}:${book.id}:${lang}`
      : null;
    const installId = ownerScopedId || craftprintInstallId;
    if (isAuthenticated && installId) {
      // No optimistic success: show "Saving…" and only flip on a real 200.
      const key = String(ideaId);
      const patchIdea = (extra) => setCompletions((c) => ({ ...c, [key]: { ...(c[key] || {}), ...extra } }));
      patchIdea({ pending: true, error: null });
      try {
        await waitForCraftprint(craftprintReadyRef.current);
        const res = await withNotFoundRetry(() => axios.post(
          `${API_BASE}/craftprint/${encodeURIComponent(installId)}/${ideaId}/complete`,
          { done: nextDone },
          { headers: authHeaders(), timeout: AXIOS_TIMEOUT_MS }
        ));
        setCompletions(res.data.completions);
        return true;
      } catch (e) {
        patchIdea({ pending: false, error: "Couldn't save today's practice. Please try again." });
        return false;
      }
    }
    const updated = toggleAnonCompletion(book.id, lang, ideaId, nextDone);
    setCompletions((c) => ({ ...c, [String(ideaId)]: updated }));
    return true;
  }, [book, lang, isAuthenticated, user, craftprintInstallId, authHeaders]);

  const craftprintById = useMemo(() => {
    const m = new Map();
    for (const cp of craftprints) m.set(Number(cp.idea_id), cp);
    return m;
  }, [craftprints]);

  const handleEditField = useCallback(async (ideaId, field, newValue) => {
    if (!book) return;
    // Logged-in users PATCH their real owner-scoped id — never "anon"
    // (the stale preview-journey key), which 404s and drops the edit.
    const installId = (isAuthenticated && user?.id)
      ? `${user.id}:${book.id}:${lang}`
      : (craftprintInstallId || `${craftprints[0]?.owner_id || "anon"}:${book.id}:${lang}`);
    try {
      await waitForCraftprint(craftprintReadyRef.current);
      const res = await withNotFoundRetry(() => axios.patch(
        `${API_BASE}/craftprint/${encodeURIComponent(installId)}/${ideaId}`,
        { [field]: newValue },
        { headers: authHeaders(), timeout: AXIOS_TIMEOUT_MS }
      ));
      if (res.data?.craftprints) setCraftprints(res.data.craftprints);
      return true;
    } catch (e) {
      return false;
    }
  }, [book, lang, isAuthenticated, user, craftprints, craftprintInstallId, authHeaders]);

  useEffect(() => {
    if (!book || !hasCommit) return;
    fetchIdeas();
  }, [book, hasCommit, fetchIdeas]);

  // ── Translate ideas when a non-English language is active ────────
  useEffect(() => {
    if (!book) return;
    if (!Array.isArray(ideas) || ideas.length === 0) {
      setTranslatedIdeas(null);
      setIsTranslatingIdeas(false);
      return;
    }
    if (lang === "en") {
      setTranslatedIdeas(null);
      setIsTranslatingIdeas(false);
      return;
    }
    const cacheKey = `${book.id}::${lang}`;
    if (translateCacheRef.current[cacheKey]) {
      setTranslatedIdeas(translateCacheRef.current[cacheKey]);
      setIsTranslatingIdeas(false);
      return;
    }
    let cancelled = false;
    setIsTranslatingIdeas(true);
    (async () => {
      try {
        const res = await withTransientRetry(() =>
          axios.post(
            `${API_BASE}/ideas-translate`,
            {
              book_id: String(book.id),
              book_title: book.title || "",
              targetLanguage: lang,
              ideas: ideas.map((i) => ({
                id: i.id,
                title: i.title || "",
                description: i.description || "",
              })),
            },
            { timeout: AXIOS_TIMEOUT_MS }
          )
        );
        if (cancelled) return;
        const payload = res.data;
        if (payload?.lang === lang && Array.isArray(payload.ideas)) {
          translateCacheRef.current[cacheKey] = payload.ideas;
          setTranslatedIdeas(payload.ideas);
        } else {
          setTranslatedIdeas(null);
        }
      } catch (e) {
        if (!cancelled) {
          setTranslatedIdeas(null);
        }
      } finally {
        if (!cancelled) setIsTranslatingIdeas(false);
      }
    })();
    return () => { cancelled = true; };
  }, [book, ideas, lang]);

  // Filter ideas → 3 committed ids, IN COMMITTED ORDER, with
  // translated title/description overlayed when a non-EN language
  // resolved successfully.
  const practices = useMemo(() => {
    if (!hasCommit || ideas.length === 0) return [];
    const trMap = translatedIdeas
      ? new Map(translatedIdeas.map((t) => [Number(t.id), t]))
      : null;
    const byId = new Map(ideas.map((i) => [i.id, i]));
    return commit.committed
      .map((id) => {
        const eng = byId.get(id);
        if (!eng) return null;
        const tr = trMap ? trMap.get(Number(id)) : null;
        return tr
          ? { ...eng, title: tr.title || eng.title, description: tr.description || eng.description }
          : eng;
      })
      .filter(Boolean);
  }, [ideas, translatedIdeas, commit.committed, hasCommit]);

  const pendingReflections = practices.filter((idea) => {
    const cp = craftprintById.get(Number(idea.id)) || {};
    return !(cp.phala_edited_at && (cp.phala || "").trim() && cp.vighna_edited_at && (cp.vighna || "").trim());
  }).length;

  // Deep link from the Siddhi gate: ?focus=phala-2 → scroll to + focus that field.
  const focusTarget = params.get("focus");
  useEffect(() => {
    if (!focusTarget || craftprints.length === 0) return;
    const [field, id] = focusTarget.split("-");
    const t = window.setTimeout(() => {
      const el = document.getElementById(`sadhana-${field}-${id}`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        el.focus({ preventScroll: true });
      }
    }, 300);
    return () => window.clearTimeout(t);
  }, [focusTarget, craftprints.length]);


  const missingCount = hasCommit && ideas.length > 0 ? RULE_OF_N - practices.length : 0;

  // ────────────────────────────────────────────────────────────────
  //  RENDER — decide which of three states to render
  // ────────────────────────────────────────────────────────────────

  return (
    <div
      data-testid="sadhana-page"
      className="min-h-screen flex flex-col sadhana-page-root"
      style={{ background: "transparent" }}
    >
      <ParchmentBackground />
      <Navigation activePath={R.sadhana} />

      <main
        className="flex-1 w-full"
        style={{ paddingTop: 88, paddingBottom: 64, background: "transparent" }}
      >
        {/* State (c) — no book */}
        {!book && (
          <div className="sadhana-empty-wrap" style={{ position: "relative", minHeight: 1025 }}>
            <StageEmptyStateFlank stage="sadhana" />
            <EmptyState />
          </div>
        )}

        {/* State (b) — book but no commit */}
        {book && !hasCommit && !commitChecking && <NeedsSankalpa book={book} />}

        {/* State (a) — book with commit */}
        {book && hasCommit && (
          <div
            className="sadhana-shell"
            style={{
              position: "relative",
              maxWidth: 1160,
              margin: "0 auto",
              padding: "0 32px",
              display: "grid",
              gridTemplateColumns: "260px 1fr",
              gap: 40,
              alignItems: "flex-start",
            }}
          >
            <CurrentBookPanel book={book} committedAt={commit.committedAt} />

            <section>
              <div style={{ textAlign: "center", marginBottom: 22 }}>
                <div style={{ display: "flex", justifyContent: "center", marginBottom: 12 }}>
                  <GoldRule wide />
                </div>
                <StageKicker stage="sadhana" text="SADHANA · साधना" testId="sadhana-header-kicker" />
                <h1
                  className="m-0 sadhana-header-h1"
                  style={{
                    fontFamily: T.F_DISPLAY,
                    fontSize: 40,
                    fontWeight: 700,
                    color: T.DARK_BROWN,
                    lineHeight: 1.1,
                    marginBottom: 10,
                  }}
                >
                  Your three daily practices.
                </h1>
                <p
                  className="m-0"
                  style={{
                    fontFamily: T.F_BODY,
                    fontStyle: "italic",
                    fontSize: 16,
                    color: T.MUTED_BROWN,
                    lineHeight: 1.55,
                    maxWidth: 620,
                    margin: "0 auto 6px",
                  }}
                >
                  From <strong>{book.title}</strong>, the three you committed to.
                  Return to this page daily. Practice, do not measure.
                </p>
              </div>

              {loading && <LoadingBanner bookTitle={book.title} />}
              {error && !loading && <ErrorState bookTitle={book.title} onRetry={fetchIdeas} />}

              {(loading || practices.length > 0) && !error && (
                <div
                  data-testid="sadhana-practice-list"
                  style={{ display: "flex", flexDirection: "column", gap: 18 }}
                >
                  {loading
                    ? Array.from({ length: RULE_OF_N }).map((_, i) => (
                        <PracticeCard key={`skel-${i}`} isSkeleton />
                      ))
                    : practices.map((idea, i) => (
                        <PracticeCard
                          key={idea.id}
                          idea={idea}
                          craftprint={craftprintById.get(Number(idea.id))}
                          index={i}
                          completion={completions[String(idea.id)]}
                          onToggleComplete={handleToggleComplete}
                          onEdit={(field, newValue) => handleEditField(idea.id, field, newValue)}
                          bookTitle={book.title}
                          bookCover={book.coverUrl || book.image}
                          day={daysSinceInstall}
                        />
                      ))}
                </div>
              )}

              {/* Advisory when some committed ids can't be found */}
              {!loading && !error && missingCount > 0 && (
                <p
                  data-testid="sadhana-missing-advisory"
                  className="m-0"
                  style={{
                    marginTop: 22,
                    padding: "12px 18px",
                    background: "rgba(139,58,18,0.08)",
                    border: `1px solid rgba(139,58,18,0.3)`,
                    borderRadius: 8,
                    fontFamily: T.F_BODY,
                    fontStyle: "italic",
                    fontSize: 13,
                    color: T.DARK_BROWN,
                    textAlign: "center",
                  }}
                >
                  {missingCount} of your {RULE_OF_N} commitments could not be found.{" "}
                  <Link
                    to={`/sankalpa?book=${book.id}`}
                    style={{ color: T.DARK_BROWN, textDecorationColor: T.GOLD }}
                  >
                    Return to Sankalpa to re-seal.
                  </Link>
                </p>
              )}

              {/* Siddhi entry — active once the page is wired */}
              {!loading && !error && practices.length > 0 && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: 8,
                    marginTop: 44,
                    paddingTop: 24,
                    borderTop: `1px dashed ${T.GOLD}`,
                  }}
                >
                  <Link
                    to={`/siddhi?book=${book.id}`}
                    data-testid="sadhana-reach-siddhi-cta"
                    data-pending={pendingReflections}
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "12px 28px",
                      fontFamily: T.F_HEADING,
                      fontSize: 13,
                      letterSpacing: "0.10em",
                      color: T.DARK_BROWN,
                      background: "rgba(240,230,204,0.8)",
                      border: `1px solid ${T.GOLD}`,
                      borderRadius: 999,
                      textDecoration: "none",
                    }}
                  >
                    Reach Siddhi <ArrowRight size={14} strokeWidth={1.75} />
                  </Link>
                  <p
                    className="m-0"
                    data-testid="sadhana-reflections-pending"
                    style={{
                      fontFamily: T.F_BODY,
                      fontStyle: "italic",
                      fontSize: 12,
                      color: T.MUTED_BROWN,
                      letterSpacing: "0.02em",
                    }}
                  >
                    {pendingReflections > 0
                      ? `${pendingReflections} of ${practices.length} practice${practices.length === 1 ? "" : "s"} still need${pendingReflections === 1 ? "s" : ""} Phala & Vighna reflections.`
                      : "All reflections named — your Siddhi is ready."}
                  </p>
                </div>
              )}

              {/* Static footer note (display-only intent copy) */}
              {!loading && !error && practices.length > 0 && (
                <p
                  className="m-0"
                  style={{
                    marginTop: 32,
                    textAlign: "center",
                    fontFamily: T.F_BODY,
                    fontStyle: "italic",
                    fontSize: 13,
                    color: T.MUTED_BROWN,
                    letterSpacing: "0.02em",
                  }}
                >
                  Sadhana is quiet work — practice, do not measure.
                </p>
              )}
            </section>
          </div>
        )}

        {/* Decorative bottom banner — only rendered when a book context
            exists, so it doesn't sit under the bare empty state. */}
        {book && <StageBottomBanner stage="sadhana" />}
      </main>

      <Footer activePath={R.sadhana} />

      {/* ── Mobile responsive pass (<900px) — mirrors Bodha/Sankalpa ── */}
      <style>{`
        .sadhana-page-root { overflow-x: hidden; }
        @media (max-width: 899px) {
          .stage-flank, .stage-bottom-banner, .stage-empty-flank { display: none !important; }
          .sadhana-empty-wrap { min-height: 0 !important; }
          .sadhana-shell {
            grid-template-columns: 1fr !important;
            gap: 24px !important;
            padding: 0 20px !important;
            width: 100% !important;
            max-width: 100% !important;
          }
          .sadhana-shell > * { min-width: 0 !important; }
          .sadhana-current-book {
            position: static !important;
            max-height: none !important;
            max-width: 320px !important;
            margin: 0 auto !important;
            width: 100% !important;
          }
          [data-testid="sadhana-practice-list"] > * { min-width: 0 !important; }
          .sadhana-header-h1 { font-size: 30px !important; line-height: 1.15 !important; }
          .sadhana-empty-h1  { font-size: 32px !important; }
        }
      `}</style>
    </div>
  );
}
